import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Table } from "shared/app/Table";
import moment from "moment";
import { Dispatch, FC, SetStateAction, useMemo } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { DateTimeTableCell } from "shared/components/DateTimeTableCell";
import { Label, SelectPayment } from "./SelectPayment";
import { useNavigate } from "react-router-dom";
import { AccountStatusBadge } from "./AccountStatusBadge";
import {
  AccountStatusVariant,
  PaymentStatuses,
  getEmptyState
} from "../constants";
import { Renter, SelectValue, Verification } from "../types";
import { useAuthContext } from "providers/Auth";
import { RvStatusBadge } from "./RvStatusBadge";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { getDisplayAccountStatus } from "../utils";
import { formatDate } from "views/rent-reporting-id/slices/SubscriptionInfo/slices/RentReportingTable/utils";

type Props = {
  rentersData: Renter[];
  hasSearchParams?: boolean;
  selectDefaultValue: PaymentStatuses;
  selectValues: SelectValue[];
  setSelectValues: Dispatch<SetStateAction<SelectValue[]>>;
  isLoading: boolean;
  sorted: { key: string; desc: boolean };
  setSorted: (sorted: { key: string; desc: boolean }) => void;
  activeTab: AccountStatusVariant;
  isReportingSingleRenter: boolean;
  reportSingleRenter: (data: {
    subscriberId: number;
    verificationId: number;
    verifications: Verification;
  }) => void;
};

export const SubscribersTable: FC<Props> = ({
  hasSearchParams,
  rentersData,
  selectDefaultValue,
  selectValues,
  setSelectValues,
  isLoading,
  sorted,
  setSorted,
  activeTab,
  reportSingleRenter,
  isReportingSingleRenter
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const navigate = useNavigate();
  const { type, permissions } = useAuthContext();

  const handleChange = (id: number, newValue: string | number) => {
    setSelectValues((prev: SelectValue[]) => {
      const existingItemIndex = prev.findIndex((item) => item.id === id);
      if (newValue === selectDefaultValue)
        return prev.filter((item) => item.id !== id);
      else if (existingItemIndex !== -1)
        return prev.map((item, index) =>
          index === existingItemIndex ? { ...item, action: newValue } : item
        );
      else return [...prev, { id, action: newValue }];
    });
  };

  const handleReportClick = (renter: any) => {
    const selectedRenter = selectValues.find(
      (r) => r.id === renter.verification_id
    );
    const verifications = {
      action: !selectedRenter
        ? selectDefaultValue
        : selectedRenter.action === PaymentStatuses.PAID_ON_TIME
          ? PaymentStatuses.PAID_ON_TIME
          : selectedRenter.action === PaymentStatuses.NO_DATA
            ? PaymentStatuses.NO_DATA
            : PaymentStatuses.DELINQUENCY,
      months_delinquency:
        selectedRenter?.action === PaymentStatuses.PAID_ON_TIME ||
        selectedRenter?.action === PaymentStatuses.NO_DATA
          ? undefined
          : selectedRenter?.action
    };
    reportSingleRenter({
      subscriberId: renter.id,
      verificationId: renter.verification_id,
      verifications
    });
    setSelectValues([]);
  };

  const columns = useMemo(() => {
    const enrollmentColumns = [
      {
        id: "status fixed_width",
        header: "Enrollment status",
        accessorKey: "type",
        size: 165,
        cell: (p) => {
          const renter = p.row.original;
          return (
            <div className="min-w-0 flex items-center">
              <AccountStatusBadge status={getDisplayAccountStatus(renter)} />
            </div>
          );
        }
      },
      {
        id: "enrollment_created fixed_width",
        header: "Enrollment created",
        accessorKey: "active_enrollment.created_at",
        size: 165,
        cell: (p) => (
          <div className="[&>p]:space-y-0">
            <DateTimeTableCell value={p.getValue()} />
          </div>
        )
      },
      {
        id: "enrollment_approved fixed_width",
        header: "Enrollment approved",
        accessorKey: "active_enrollment.approved_at",
        size: 165,
        cell: (p) => (
          <div className="[&>p]:!space-y-0">
            <DateTimeTableCell value={p.getValue()} />
          </div>
        )
      },
      {
        id: "lease_period fixed_width",
        header: "Lease period",
        accessorKey: "lease_start_date",
        size: 165,
        cell: (p) => {
          const renter = p.row.original as any;
          const leaseStart = renter.lease_start_date || renter.move_in_date;
          const leaseEnd = renter.lease_end_date;
          const startDate = formatDate(leaseStart, "MMM DD, YYYY", "--");
          const endDate = formatDate(leaseEnd, "MMM DD, YYYY", "--");
          return (
            <div className="min-w-0 flex flex-col text-sm font-medium text-content-1000 h-full justify-center">
              <span className="overflow-hidden text-ellipsis whitespace-nowrap leading-tight">
                {startDate}
              </span>
              <span className="overflow-hidden text-ellipsis whitespace-nowrap leading-tight">
                {endDate}
              </span>
            </div>
          );
        }
      }
    ];
    const cols: ColumnDef<Renter, any>[] = [
      {
        id: "id fixed_width",
        header: "Renter",
        accessorKey: "name",
        size: 209,
        cell: (p) => {
          return (
            <span className="text-sm text-content-1000 font-medium block overflow-hidden text-ellipsis whitespace-nowrap">
              {p.getValue()}
            </span>
          );
        }
      }
    ];
    if (type.isSDK)
      cols.push(
        {
          id: "email fixed_width",
          header: "Email",
          accessorKey: "email",
          size: 200,
          cell: (p) => {
            return (
              <span className="text-sm text-content-500 block overflow-hidden text-ellipsis whitespace-nowrap">
                {p.getValue() ? p.getValue() : "--"}
              </span>
            );
          }
        },
        ...enrollmentColumns
      );
    else
      cols.push(
        {
          id: "property fixed_width",
          header: dictionary.Property,
          accessorKey: "property",
          size: 209,
          cell: (p) => {
            const subscriber = p.row.original;
            return (
              <div className="min-w-0 h-full max-w-[185px] flex flex-col justify-center">
                <p className="text-sm text-content-1000 overflow-hidden text-ellipsis whitespace-nowrap leading-tight">
                  {p.getValue()}
                </p>
                <p className="text-xs text-content-400 overflow-hidden text-ellipsis whitespace-nowrap leading-tight">
                  {subscriber.address}
                </p>
              </div>
            );
          }
        },
        ...enrollmentColumns,
        {
          id: "reporting fixed_width",
          header: `${moment().subtract(1, "months").format("MMMM")} status`,
          accessorKey: "type",
          size: 234,
          cell: (p) => {
            const id = p.row.original.verification_id;
            const delinquencyOptions =
              p.row.original.available_delinquency_options;
            const options = delinquencyOptions?.length
              ? delinquencyOptions.map((option) => {
                  return {
                    value: option.months_delinquency,
                    label: (
                      <Label
                        title={option.name}
                        icon={
                          <div className="bg-danger-50 h-5 w-5 flex items-center justify-center">
                            <p className="text-danger-500 text-sm font-bold">
                              {option.months_delinquency}
                            </p>
                          </div>
                        }
                      />
                    )
                  };
                })
              : [];

            const isNeedToReport =
              p.getValue() === AccountStatusVariant.NeedToReport &&
              permissions.boomReportWriteAccess &&
              !type.isSDK;

            return (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                className="flex items-center gap-5 max-w-[200px]"
              >
                {isNeedToReport ? (
                  <>
                    <SelectPayment
                      id={String(id)}
                      value={
                        selectValues.find((item) => item.id === id)?.action ||
                        selectDefaultValue
                      }
                      handleChange={(e) => handleChange(id, e.target.value)}
                      options={options}
                    />
                    <Button
                      variant={ButtonVariant.PRIMARY}
                      size={ButtonSize.XS}
                      loading={isReportingSingleRenter}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        !isReportingSingleRenter &&
                          handleReportClick(p.row.original);
                      }}
                    >
                      Report
                    </Button>
                  </>
                ) : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                p.row.original.rv_status ? (
                  <RvStatusBadge status={p.row.original.rv_status} />
                ) : (
                  "--"
                )}
              </div>
            );
          }
        }
      );

    return cols;
  }, [selectDefaultValue, selectValues, isReportingSingleRenter, permissions]);

  const table = useReactTable({
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    data: rentersData || [],
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <Table
      table={table}
      isLoading={isLoading}
      tdClassName="!py-2"
      rowHeight="76px"
      tableClassName="overflow-x-auto"
      onRowClick={(id) => navigate(`/rent-reporting/${id}`)}
      emptyStateTitle={
        hasSearchParams
          ? "No results found"
          : getEmptyState(activeTab, type.isSDK).title
      }
      emptyStateText={getEmptyState(activeTab, type.isSDK).subtitle}
      EmptyStateIcon={getEmptyState(activeTab, type.isSDK).icon}
      sortableKeys={["id"]}
      sorted={sorted}
      setSorted={setSorted}
    />
  );
};
